interface SliderPreviewProps {
  message?: string;
  description?: string;
  minLabel?: string;
  maxLabel?: string;
  min: number;
  max: number;
  increment: number;
  labels?: string[];
}

const MAX_RENDERED_TICKS = 101;

export const SliderPreview = ({
  message,
  description,
  minLabel,
  maxLabel,
  min,
  max,
  increment,
  labels,
}: SliderPreviewProps) => {
  // Labeled mode: non-empty labels array drives derived numeric params
  const isLabeled = Array.isArray(labels) && labels.length > 0;
  const effMin = isLabeled ? 0 : min;
  const effMax = isLabeled ? labels.length - 1 : max;
  const effIncrement = isLabeled ? 1 : increment;

  const tickLabelAt = (tickIndex: number): string | null => {
    if (isLabeled) {
      // tickIndex is the tick's position in the ticks array (0 = top = max, last = bottom = min)
      // ticks render top-to-bottom; tick 0 maps to effMax label, last maps to effMin label
      const labelIndex = (labels.length - 1) - tickIndex;
      return labels[labelIndex] ?? null;
    }
    return null;
  };

  const rangeValid = Number.isFinite(effMin) && Number.isFinite(effMax) && effMax > effMin;
  const stepValid = Number.isFinite(effIncrement) && effIncrement > 0;

  const tickCount =
    rangeValid && stepValid
      ? Math.min(Math.floor((effMax - effMin) / effIncrement) + 1, MAX_RENDERED_TICKS)
      : 0;

  const ticks = Array.from({ length: tickCount }, (_, i) => i);

  return (
    <div className="rounded-md border border-gray-200 p-3 text-sm">
      {message ? <div className="font-bold text-base text-black">{message}</div> : null}
      {description ? <div className="mt-1 text-gray-500">{description}</div> : null}

      <div className="mt-2 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-stretch gap-2">
        {isLabeled ? (
          // Labeled mode: left column shows each label aligned to its tick
          <div className="flex min-w-0 flex-col justify-between py-1 text-right text-xs text-gray-500" style={{ height: '10rem' }}>
            {ticks.map((t) => {
              const lbl = tickLabelAt(t);
              return (
                <span key={t} className="truncate leading-none" title={lbl ?? ''}>
                  {lbl}
                </span>
              );
            })}
          </div>
        ) : (
          // Numeric mode: left column shows only endpoint captions
          <div className="flex min-w-0 flex-col justify-between py-1 text-right text-xs text-gray-500">
            <span className="truncate" title={maxLabel ?? ''}>{maxLabel ?? (rangeValid ? String(effMax) : '-')}</span>
            <span className="truncate font-medium text-blue-600" title={minLabel ?? ''}>{minLabel ?? (rangeValid ? String(effMin) : '-')}</span>
          </div>
        )}

        <div className="relative flex h-40 w-6 items-end justify-center">
          <div className="absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 rounded-full bg-gray-200" />
          <div className="absolute inset-y-0 left-1/2 flex -translate-x-1/2 flex-col justify-between py-1">
            {ticks.map((t) => (
              <span key={t} className="block h-px w-2 bg-gray-300" />
            ))}
          </div>
          <div className="z-10 mb-0 h-5 w-5 rounded-full border-2 border-blue-600 bg-white" />
        </div>

        <div className="flex min-w-0 flex-col items-start justify-end">
          {/* Labeled (message-type) sliders have no numeric value: the positions
              map to category labels (shown on the left), so a value bubble here
              would render a misleading default "0". Only numeric sliders show it. */}
          {!isLabeled ? (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
              {rangeValid ? effMin : '-'}
            </div>
          ) : null}
        </div>
      </div>

      {!isLabeled && (!rangeValid || !stepValid) ? (
        <div className="mt-2 text-xs text-gray-400">Set valid Min, Max, and Increment to preview.</div>
      ) : null}
    </div>
  );
};
